import Clickable from "@components/clickable";
import {
    getDrillDownNavParams,
    getIntentNavParams,
    getNavLinkComponent,
    getTableLink, IGetDrillDownParams
} from "@components/drillDown/DrillDown.utils";
import { StatusNoIcon, StatusYesIcon } from "@components/icon";
import { getSimpleBoolItemLabel, IGetValueArgs } from "@components/smart/FieldInfo";
import { withDisplayName } from "@components/smart/GeneralFieldDefinition";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import {
    fetchValueHelperData,
    IFetchValueHelperItemsOptions
} from "@components/smart/smartValueHelper/ValueHelper.utils";
import { ICellValueObject } from "@components/table";
import { getRouteByEntityType } from "@odata/EntityTypes";
import {
    ChatEntity,
    ChatMessageAttachmentEntity,
    ChatMessageEntity,
    ChatParticipantRecipientEntity,
    ChatParticipantSenderEntity,
    EntitySetName,
    EntityTypeName,
    FileMetadataEntity,
    IChatEntity,
    IUserEntity,
    UserEntity
} from "@odata/GeneratedEntityTypes";
import { IFormatOptions } from "@odata/OData.utils";
import { CustomerGeneralRoleId, isUserCustomer } from "@pages/admin/users/Users.utils";
import {
    CUSTOMER_GROUP,
    getParticipantQuery,
    getUserSelectGroups,
    ISendToAccountantChatData,
    USER_GROUP
} from "@pages/chats/Chats.utils";
import ChatsFormView from "@pages/chats/ChatsFormView";
import { enhancedDocumentTypeWithDDOPP, getDocumentTypeName } from "@pages/documents/Document.utils";
import { logger } from "@utils/log";
import { getOneFetch } from "@utils/oneFetch";
import i18next from "i18next";
import { ITableStorageDefaultCustomData, TableStorage } from "model/TableStorage";
import React from "react";

import { NEW_ITEM_DETAIL } from "../../constants";
import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { FieldType, IconSize, Sort, TextAlign } from "../../enums";
import { TRecordAny, TValue } from "../../global.types";
import { Model } from "../../model/Model";
import BindingContext, { createPath, IEntity, TEntityKey } from "../../odata/BindingContext";
import DateType, { DateFormat, getLocalTimeDayjs, getUtcDayjs } from "../../types/Date";
import { IFormDef } from "../../views/formView/Form";
import { ISplitPageTableDef, ITableDefTabData } from "../../views/table/TableView.utils";
import { userNameWithAvatarFormatter, userNameWithAvatarTableFormatter } from "../admin/users/Users.utils";
import { setDefByEntityType } from "../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../PageUtils";


export interface IChatCustomData extends ITableStorageDefaultCustomData {
    isInDialog?: boolean;
    user?: IEntity;
    numberOurs?: string;
    // for chats linked with document
    documentBindingContext?: BindingContext;
    isDraft?: boolean;
    id?: TEntityKey;
    entityType?: EntityTypeName;
    onClose?: () => void;
    chatData?: ISendToAccountantChatData;
    useCustomMessages?: boolean;
}

const userColDef = (propName: string, label: string) => {
    return {
        label,
        textAlign: TextAlign.Left,
        additionalProperties: [{ id: UserEntity.FirstName }, { id: UserEntity.LastName }],
        formatter: (val: TValue, args: IFormatOptions) => {
            return userNameWithAvatarTableFormatter(val, {
                ...args,
                entity: args.entity?.[propName]?.User
            });
        }
    };
};

export const TAB_MINE = "Mine";
export const TAB_OTHERS = "Others";

const typeFormatter = (val: TValue, args: IFormatOptions): string => {
    if (val === "InboxFile") {
        return i18next.t("Chat:Table.InboxFile");
    }
    return val ? enhancedDocumentTypeWithDDOPP({
        DocumentTypeCode: args.item.LinkedEntityType,
        ...args.item
    }, getDocumentTypeName(val as EntityTypeName)) : "";
};

interface IGetDefArgs {
    isDialog?: boolean
}

export const getDefinitions = (context: IAppContext, args?: IGetDefArgs): IDefinition => {
    const isCustomer = isUserCustomer(context.getData().userSettings);
    const propRecipientName = ChatEntity.Recipient;
    const propSenderName = ChatEntity.Sender;

    const isDialog = args?.isDialog ?? false;

    const getLinkData = (val: TValue, entity: IEntity, args: IFormatOptions, isTable?: boolean) => {
        const cd = (args.storage.getCustomData() as IChatCustomData);
        const isDraft = entity.IsLinkedEntityDraft || cd?.isDraft;

        if (val || isDraft) {
            let lbl = val as string;


            const id = isDraft ? NEW_ITEM_DETAIL : entity.LinkedEntityId ?? cd?.id;
            const et = entity.LinkedEntityType ?? cd?.entityType;

            const isCustomer = isUserCustomer(context.getData().userSettings);
            const isInbox = et === EntityTypeName.InboxFile;

            if (isInbox) {
                lbl = `${i18next.t("Chat:Inbox")}: ${lbl}`;

                if (isCustomer) {
                    return lbl;
                }
            }

            let customQueryString = "";
            if (isDraft) {
                const draftLbl = i18next.t("Chat:Form.Draft");
                lbl = val ? `${val} (${draftLbl})` : draftLbl;
                customQueryString = `drafts=true&draftId=${entity.LinkedEntityId}`;
            }

            let route = `${getRouteByEntityType(et)}`;
            if (!isInbox) {
                route += `/${id}`;
            } else {
                if (entity.InboxFileEntityType) {
                    route += `/${entity.InboxFileEntityType}`;
                }
                customQueryString = `filter={"Sender":"${entity.LinkedEntityDescriptor}"}`;
            }

            const _args: IGetDrillDownParams = {
                route,
                customQueryString,
                context: args.storage.context,
                storage: args.storage
            };

            if (!isDialog) {
                const fn = isInbox ? getDrillDownNavParams : getIntentNavParams;
                return isTable ? getTableLink(lbl, _args, fn) : getNavLinkComponent(lbl, _args, fn);
            } else {
                const _params = getIntentNavParams(_args);
                const _click = () => {
                    (args.storage.getCustomData() as IChatCustomData).onClose?.();
                };

                const value = (
                    <Clickable
                        link={_params}
                        onClick={_click}>
                        {lbl}
                    </Clickable>
                );

                return isTable ? {
                    value,
                    tooltip: lbl
                } : value;
            }
        }

        return undefined;
    };


    const getParticipantSummary = () => {
        return {
            id: propRecipientName,
            formatter: (val: TValue, args: IFormatOptions<IChatEntity>) => {
                const cd = (args.storage.getCustomData() as IChatCustomData);
                const user = args.entity?.[propRecipientName]?.User as IEntity ?? cd.user;
                return (userNameWithAvatarFormatter(val ?? user?.id, {
                    entity: user
                }, {
                    isBig: true
                }) as ICellValueObject)?.value;
            }
        };
    };

    const sender = createPath(ChatEntity.Sender, ChatParticipantSenderEntity.User);
    const recipient = createPath(ChatEntity.Recipient, ChatParticipantRecipientEntity.User);
    const filterFetch = getOneFetch();

    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        defaultFilters: [
            ChatEntity.Title,
            ChatEntity.LinkedEntityDescriptor,
            sender,
            recipient,
            BindingContext.localContext("Participant")
        ],
        filterDefinition: {
            [BindingContext.localContext("Participant")]: {
                type: FieldType.MultiSelect,
                label: i18next.t("Chat:Attendant"),
                additionalProperties: [{ id: "/Sender/User/Id" }, { id: "/Recipient/User/Id" }],
                filterName: ["/Sender/User/Id", "/Recipient/User/Id"],
                isValueHelp: false,
                fieldSettings: {
                    groups: getUserSelectGroups(),
                    cacheFetchedItemsInFieldInfo: false,
                    itemsFactory: async (args: IGetValueArgs) => {
                        const { storage, bindingContext } = args;
                        const fieldInfo = storage.getInfo(bindingContext);

                        const opts: IFetchValueHelperItemsOptions = {
                            storage: storage as TableStorage,
                            bindingContext, fieldInfo,
                            isEnum: false
                        };

                        const data = (await fetchValueHelperData(opts, filterFetch)).data;
                        const set: Set<string> = new Set();
                        for (const item of data || []) {
                            set.add(item?.Recipient?.User?.Id);
                            set.add(item?.Sender?.User?.Id);
                        }

                        // fetch user roles
                        try {
                            const ids = Array.from(set.keys());
                            if (ids?.length > 0) {
                                const res = await storage.oData.getEntitySetWrapper(EntitySetName.Users).query()
                                    .filter(`${UserEntity.Id} in (${ids.join(',')})`)
                                    .expand(UserEntity.GeneralRole)
                                    .fetchData<IUserEntity[]>();

                                const users: IUserEntity[] = res.value?.sort?.((u1: IUserEntity, u2: IUserEntity) => {
                                    // first sort by role (customer first) and then by name
                                    const u1Role = u1.GeneralRole?.Id;
                                    const u2Role = u2.GeneralRole?.Id;
                                    if (u1Role !== u2Role) {
                                        return u1Role === CustomerGeneralRoleId ? -1 : 1;
                                    }

                                    return u1.Name?.localeCompare(u2.Name);
                                });

                                return users?.map(user => {
                                    return {
                                        id: user.Id?.toString(),
                                        label: user.Name,
                                        groupId: user.GeneralRole?.Id === CustomerGeneralRoleId ? CUSTOMER_GROUP : USER_GROUP
                                    };
                                });
                            }
                        } catch (e) {
                            logger.error("error fetching grouped user data", e);
                        }

                        return null;
                    }
                }
            },
            [ChatEntity.Title]: {},
            [ChatEntity.IsLinkedEntityDraft]: {},
            [ChatEntity.LinkedEntityDescriptor]: {},
            [ChatEntity.LinkedEntityType]: {
                formatter: typeFormatter
            },
            [ChatEntity.DateLastMessage]: {
                label: i18next.t("Chat:Table.LastMessage")
            },
            ...withDisplayName(sender),
            ...withDisplayName(recipient)
        },
        isValueHelp: true
    }];

    const tabs: ITableDefTabData[] = [{
        id: TAB_MINE,
        title: i18next.t("Chat:Table.Mine"),
    }, {
        id: TAB_OTHERS,
        title: i18next.t("Chat:Table.Others")
    }];

    const filter = (args: IGetValueArgs, otherArgs?: TRecordAny) => {
        const filter: string[] = [];

        if (isDialog) {
            const cd = args.storage.getCustomData() as IChatCustomData;
            const id = cd.id;
            const draftSuffix = cd.isDraft ? ` AND ${ChatEntity.IsLinkedEntityDraft} eq true ` : "";

            filter.push(`(${ChatEntity.LinkedEntityId} eq ${id} AND ${ChatEntity.LinkedEntityType} eq '${cd.entityType}' ${draftSuffix})`);
        }

        if (isCustomer) {
            filter.push(getParticipantQuery(args.storage.context.getData().userSettings.Id));
        }

        return filter.length > 0 ? `(${filter?.join(" AND ")})` : null;
    };

    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.Chat}Table`,
        filterBarDef,
        filter,
        initialSortBy: [{ id: ChatEntity.DateLastMessage, sort: Sort.Desc }],
        tabs: isCustomer ? null : tabs,
        additionalProperties: [{
            id: ChatEntity.IsTaxDocument
        }, {
            id: createPath(ChatEntity.Recipient, ChatParticipantRecipientEntity.User)
        }, {
            id: createPath(ChatEntity.Recipient, ChatParticipantRecipientEntity.User, UserEntity.GeneralRole)
        }, {
            id: createPath(ChatEntity.Sender, ChatParticipantSenderEntity.DateLastRead)
        }, {
            id: createPath(ChatEntity.Sender, ChatParticipantSenderEntity.User, UserEntity.GeneralRole)
        }, {
            id: createPath(ChatEntity.Recipient, ChatParticipantRecipientEntity.DateLastRead)
        }, {
            id: createPath(ChatEntity.Messages, ChatMessageEntity.DateCreated)
        }, {
            id: createPath(ChatEntity.Messages, ChatMessageEntity.CreatedBy)
        }, {
            id: createPath(ChatEntity.Messages, ChatMessageEntity.DateLastModified)
        }, {
            id: createPath(ChatEntity.Recipient, ChatParticipantRecipientEntity.IsFlagged)
        }, {
            id: createPath(ChatEntity.Sender, ChatParticipantSenderEntity.IsFlagged)
        },
            { id: ChatEntity.LinkedEntityType }, { id: ChatEntity.LinkedEntityId }, { id: ChatEntity.IsLinkedEntityDraft },
            { id: ChatEntity.InboxFileEntityType }],
        tabsSettings: {
            withoutCounts: true,
            customFilter: (selectedTab: string, storage) => {
                if (!selectedTab) {
                    return "";
                }

                const sender = createPath(ChatEntity.Sender, ChatParticipantSenderEntity.User, UserEntity.Id);
                const recipient = createPath(ChatEntity.Recipient, ChatParticipantSenderEntity.User, UserEntity.Id);
                const currentUserId = storage.context.getData().userSettings.Id;

                if (selectedTab === TAB_MINE) {
                    return `(${sender} eq ${currentUserId} OR ${recipient} eq ${currentUserId})`;
                }

                return `(${sender} ne ${currentUserId} AND ${recipient} ne ${currentUserId})`;
            }
        },
        columns: [
            ChatEntity.Title,
            createPath(propSenderName, ChatParticipantSenderEntity.User),
            createPath(propRecipientName, ChatParticipantRecipientEntity.User),
            BindingContext.localContext("Actualization"),
            ChatEntity.DateLastMessage,
            ChatEntity.LinkedEntityDescriptor
        ],
        columnDefinition: {
            Title: {},
            [ChatEntity.DateLastMessage]: {
                label: i18next.t("Chat:Actualization"),
                formatter: (val: TValue, args: IFormatOptions) => {
                    return DateType.localFormat(val as Date, DateFormat.dateAndTime);
                }
            },
            [createPath(propRecipientName, ChatParticipantRecipientEntity.User)]: userColDef(propRecipientName, i18next.t("Chat:Table.Recepient")),
            [createPath(propSenderName, ChatParticipantSenderEntity.User)]: userColDef(propSenderName, i18next.t("Chat:Table.Sender")),
            [ChatEntity.LinkedEntityDescriptor]: {
                label: i18next.t("Chat:Topic"),
                formatter: (val: TValue, args?: IFormatOptions) => {
                    const row = args.item;
                    return getLinkData(val, row, args, true) as ICellValueObject;
                }
            },
            [ChatEntity.LinkedEntityType]: {
                formatter: typeFormatter
            },
            [ChatEntity.IsLinkedEntityDraft]: {
                formatter: (value: TValue) => {
                    const Icon = value ? StatusYesIcon : StatusNoIcon;
                    const tooltip = getSimpleBoolItemLabel(!!value);

                    return {
                        tooltip,
                        value: (<Icon title={tooltip} height={IconSize.S} width={IconSize.S}/>)
                    };
                },
            },
            [BindingContext.localContext("Actualization")]: {
                sortId: ChatEntity.DateLastMessage,
                label: i18next.t("Chat:Table.LastMessage"),
                formatter: (val: TValue, args: IFormatOptions) => {
                    const now = getLocalTimeDayjs();
                    const count = args.item?.Messages?.length;
                    const lastMessageDate = getUtcDayjs(args.item?.Messages?.[count - 1]?.DateCreated as Date);

                    const diff = now.diff(lastMessageDate, "hours");

                    if (diff < 1) {
                        return `< 1 ${i18next.t("Common:Time.Hours", { count: 1 })}`;
                    } else if (diff < 24) {
                        const count = Math.floor(diff);
                        return `${count} ${i18next.t("Common:Time.Hours", { count })}`;
                    } else if (diff < 24 * 30) {
                        const count = Math.floor(diff / 24);
                        return `${count} ${i18next.t("Common:Time.Days", { count })}`;
                    } else if (diff < 24 * 30 * 12) {
                        const count = Math.floor(diff / (24 * 30));
                        return `${count} ${i18next.t("Common:Time.Month", { count })}`;
                    } else {
                        const count = Math.floor(diff / (24 * 30 * 12));
                        return `${count} ${i18next.t("Common:Time.Year", { count })}`;
                    }
                }
            }
        },
        title: i18next.t("Chat:Title")
    };

    const summary: ISummaryItem[] = [getParticipantSummary(),
        {
            label: i18next.t("Chat:Topic"),
            id: ChatEntity.LinkedEntityDescriptor,
            formatter: (val: TValue, args: IFormatOptions<IChatEntity>) => {
                const cd = (args.storage.getCustomData() as IChatCustomData);
                const _val = cd?.numberOurs ?? val;

                const link = getLinkData(_val, args.storage.data.entity, args, false);
                return link ?? "-";
            }
        }
    ];

    const userEntityAdditionalProps = [
        { id: UserEntity.Name },
        { id: UserEntity.LastName },
        { id: UserEntity.FirstName },
        { id: UserEntity.GeneralRole }
    ];

    const form: IFormDef = {
        id: `${EntityTypeName.Chat}Form`,
        title: i18next.t("Chat:TitleSingular"),
        summary,
        getItemBreadCrumbText: (storage: Model) =>
            getItemBreadCrumbsText(storage, i18next.t("Chat:NewChat"), storage.data.entity?.[ChatEntity.Title]),
        translationFiles: getDefinitions.translationFiles,
        formControl: ChatsFormView,
        fieldDefinition: {},
        additionalProperties: [
            { id: ChatEntity.Title },
            {
                id: createPath(ChatEntity.Sender, ChatParticipantSenderEntity.User),
                additionalProperties: userEntityAdditionalProps
            },
            {
                id: createPath(ChatEntity.Recipient, ChatParticipantRecipientEntity.User),
                additionalProperties: userEntityAdditionalProps
            }, {
                id: createPath(ChatEntity.Sender, ChatParticipantSenderEntity.IsFlagged)
            }, {
                id: createPath(ChatEntity.Recipient, ChatParticipantRecipientEntity.IsFlagged)
            }, {
                id: createPath(ChatEntity.Sender, ChatParticipantSenderEntity.DateLastRead)
            }, {
                id: createPath(ChatEntity.Recipient, ChatParticipantRecipientEntity.DateLastRead)
            }, {
                id: ChatEntity.IsLinkedEntityDraft
            }, {
                id: ChatEntity.LinkedEntityType
            }, {
                id: ChatEntity.LinkedEntityId
            }, {
                id: ChatEntity.InboxFileEntityType
            }, {
                id: ChatEntity.Messages,
                additionalProperties: [
                    { id: ChatMessageEntity.Content },
                    { id: ChatMessageEntity.DateCreated },
                    { id: ChatMessageEntity.DateLastModified },
                    {
                        id: ChatMessageEntity.CreatedBy,
                        additionalProperties: userEntityAdditionalProps
                    }, {
                        id: ChatMessageEntity.Attachments,
                        additionalProperties: [
                            { id: ChatMessageAttachmentEntity.Description },
                            {
                                id: ChatMessageAttachmentEntity.File,
                                additionalProperties: [
                                    { id: FileMetadataEntity.Id },
                                    { id: FileMetadataEntity.Name }
                                ]
                            }
                        ]
                    }
                ]
            },
        ]
    };

    return {
        entitySet: EntitySetName.Chats,
        table,
        form
    };
};

export const getDialogDefinitions: IGetDefinition = (context?: IAppContext, args?: IGetDefArgs): IDefinition => {
    return getDefinitions(context, { isDialog: args?.isDialog ?? true });
};
getDefinitions.translationFiles = ["Chat", "Common", "Error", "Components", "Document", "Users"];
getDialogDefinitions.translationFiles = [...getDefinitions.translationFiles];

setDefByEntityType(EntityTypeName.Chat, getDefinitions);
